import {
  NOTIFICATION_EVENT_DESCRIPTIONS,
  NOTIFICATION_EVENT_LABELS,
  NOTIFICATION_PRODUCT_LABELS,
  NotificationEventKey,
  type NotificationProductCode,
  type NotificationPreference,
} from '@gb-ticket/shared-types';
import { BellIcon, Loader2Icon, MailIcon, SmartphoneIcon, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { useProfileEdit } from '@/platform/features/profile/profile-edit-context';
import type { PushPermissionState } from '@/platform/features/notifications/push/push-support';
import { usePushSubscription } from '@/platform/features/notifications/push/use-push-subscription';
import { usePresentVoiceText, useVoiceCopy } from '@/platform/voice/use-voice';
import { deriveProductPresetLevel } from '@/platform/lib/profile-notification-presets';
import {
  PROFILE_NOTIFICATION_TABLE_SECTIONS,
  type ProfileNotificationTableSection,
} from '@/platform/lib/profile-notification-table-sections';
import { PROFILE_DEVICES_PATH } from '@/platform/lib/profile-paths';

type PreferenceGroup = {
  productCode: NotificationProductCode;
  eventKeys: NotificationEventKey[];
};

type PreferenceField = 'inApp' | 'email' | 'push';

const CHANNELS: { field: PreferenceField; label: string; icon: LucideIcon }[] = [
  { field: 'inApp', label: 'Glocke', icon: BellIcon },
  { field: 'email', label: 'E-Mail', icon: MailIcon },
  { field: 'push', label: 'Push', icon: SmartphoneIcon },
];

export function ProfileNotificationsPreferences() {
  const copy = useVoiceCopy();
  const {
    profile,
    items,
    preferencesPending,
    orgEmailEnabled,
    preferenceGroups,
    patchPreference,
    applyProductPreset,
    patchProductColumn,
  } = useProfileEdit();
  const push = usePushSubscription(true);
  const emailColumnDisabled = orgEmailEnabled === false;
  const pushDeviceActive = push.supported && push.isActiveOnThisDevice;

  if (preferencesPending) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Mitteilungen werden geladen …
      </p>
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      <NotificationChannelIntro
        accountEmail={profile.email}
        pushSupported={push.supported}
        pushServerEnabled={push.serverEnabled}
        pushPermission={push.permission}
        pushActiveOnDevice={push.isActiveOnThisDevice}
        pushLoading={push.isLoading}
      />

      {orgEmailEnabled === false ? (
        <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
          {copy('platform.profile.notify.org-email-off')}
        </p>
      ) : null}

      <NotificationChannelMatrix
        groups={preferenceGroups}
        items={items}
        emailColumnDisabled={emailColumnDisabled}
        showPresets
        onPreset={(productCode, preset) => applyProductPreset(productCode, preset)}
        onColumn={patchProductColumn}
        onPatch={patchPreference}
      />

      {!pushDeviceActive && push.supported && push.serverEnabled ? (
        <p className="text-sm text-muted-foreground">
          Push-Häkchen gelten erst, wenn dieses Gerät unter{' '}
          <Link
            to={PROFILE_DEVICES_PATH}
            className="text-primary underline-offset-4 hover:underline"
          >
            Geräte
          </Link>{' '}
          erlaubt ist.
        </p>
      ) : null}
    </div>
  );
}

function NotificationChannelIntro({
  accountEmail,
  pushSupported,
  pushServerEnabled,
  pushPermission,
  pushActiveOnDevice,
  pushLoading,
}: {
  accountEmail: string;
  pushSupported: boolean;
  pushServerEnabled: boolean;
  pushPermission: PushPermissionState;
  pushActiveOnDevice: boolean;
  pushLoading: boolean;
}) {
  const copy = useVoiceCopy();
  return (
    <ul className="space-y-2 rounded-md border px-4 py-3 text-sm">
      <li>
        <span className="font-medium">Glocke.</span>{' '}
        <span className="text-muted-foreground">
          Erscheint in der Anwendung unter dem Glockensymbol. Pro Ereignis schaltbar, kein globaler
          Schalter.
        </span>
      </li>
      <li>
        <span className="font-medium">E-Mail.</span>{' '}
        <span className="text-muted-foreground">
          {copy('platform.profile.notify.account-email', { email: accountEmail })}
        </span>
      </li>
      <li>
        <span className="font-medium">Push.</span>{' '}
        {pushLoading ? (
          <Loader2Icon className="inline size-4 animate-spin" aria-hidden />
        ) : (
          <span className="text-muted-foreground">
            {pushStatusSentence(
              pushSupported,
              pushServerEnabled,
              pushPermission,
              pushActiveOnDevice,
            )}
            {' · '}
            <Link
              to={PROFILE_DEVICES_PATH}
              className="text-primary underline-offset-4 hover:underline"
            >
              Geräte einrichten
            </Link>
          </span>
        )}
      </li>
    </ul>
  );
}

export function NotificationChannelMatrix({
  groups,
  items,
  emailColumnDisabled,
  showPresets,
  onPreset,
  onColumn,
  onPatch,
}: {
  groups: PreferenceGroup[];
  items: NotificationPreference[];
  emailColumnDisabled: boolean;
  showPresets: boolean;
  onPreset: (
    productCode: NotificationProductCode,
    preset: 'weniger' | 'empfohlen' | 'alle',
  ) => void;
  onColumn: (
    productCode: NotificationProductCode,
    field: 'inApp' | 'email' | 'push',
    value: boolean,
  ) => void;
  onPatch: (
    eventKey: NotificationPreference['eventKey'],
    field: 'inApp' | 'email' | 'push',
    value: boolean,
  ) => void;
}) {
  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <ProductNotificationSection
          key={group.productCode}
          group={group}
          items={items}
          emailColumnDisabled={emailColumnDisabled}
          showPresets={showPresets}
          onPreset={(preset) => onPreset(group.productCode, preset)}
          onColumn={(field, value) => onColumn(group.productCode, field, value)}
          onPatch={onPatch}
        />
      ))}
    </div>
  );
}

function ProductNotificationSection({
  group,
  items,
  emailColumnDisabled,
  showPresets,
  onPreset,
  onColumn,
  onPatch,
}: {
  group: PreferenceGroup;
  items: NotificationPreference[];
  emailColumnDisabled: boolean;
  showPresets: boolean;
  onPreset: (preset: 'weniger' | 'empfohlen' | 'alle') => void;
  onColumn: (field: 'inApp' | 'email' | 'push', value: boolean) => void;
  onPatch: (
    eventKey: NotificationPreference['eventKey'],
    field: 'inApp' | 'email' | 'push',
    value: boolean,
  ) => void;
}) {
  const presetLevel = deriveProductPresetLevel(items, group.productCode, group.eventKeys);
  const sections = PROFILE_NOTIFICATION_TABLE_SECTIONS[group.productCode] ?? [
    { title: '', eventKeys: group.eventKeys },
  ];

  const visibleKeySet = new Set(group.eventKeys);

  return (
    <section aria-labelledby={`prefs-${group.productCode}`} className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <h3
            id={`prefs-${group.productCode}`}
            className="text-sm font-semibold tracking-wide text-muted-foreground uppercase"
          >
            {NOTIFICATION_PRODUCT_LABELS[group.productCode]}
          </h3>
          {showPresets && presetLevel === 'angepasst' ? (
            <span className="text-xs text-muted-foreground">Angepasst</span>
          ) : null}
        </div>
        {showPresets ? (
          <div className="grid w-full grid-cols-3 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center">
            <PresetButton
              label="Weniger"
              active={presetLevel === 'weniger'}
              onClick={() => onPreset('weniger')}
            />
            <PresetButton
              label="Empfohlen"
              active={presetLevel === 'empfohlen'}
              onClick={() => onPreset('empfohlen')}
            />
            <PresetButton
              label="Alle Kanäle"
              active={presetLevel === 'alle'}
              onClick={() => onPreset('alle')}
            />
          </div>
        ) : null}
      </div>

      <MobilePreferenceList
        group={group}
        sections={sections}
        visibleKeySet={visibleKeySet}
        items={items}
        emailColumnDisabled={emailColumnDisabled}
        onColumn={onColumn}
        onPatch={onPatch}
      />

      <div className="hidden overflow-x-auto rounded-md border md:block">
        <table className="w-full min-w-[36rem] border-collapse text-sm">
          <thead>
            <tr className="border-b bg-muted/40">
              <th className="sticky left-0 z-10 bg-muted/40 px-3 py-2 text-left font-medium">
                Ereignis
              </th>
              <th className="px-3 py-2 text-center font-medium">
                <ColumnHeaderCheckbox
                  label="Glocke"
                  checked={columnAllChecked(items, group.eventKeys, 'inApp')}
                  onCheckedChange={(value) => onColumn('inApp', value)}
                />
              </th>
              <th className="px-3 py-2 text-center font-medium">
                <ColumnHeaderCheckbox
                  label="E-Mail"
                  checked={columnAllChecked(items, group.eventKeys, 'email')}
                  disabled={emailColumnDisabled}
                  onCheckedChange={(value) => onColumn('email', value)}
                />
              </th>
              <th className="px-3 py-2 text-center font-medium">
                <ColumnHeaderCheckbox
                  label="Push"
                  checked={columnAllChecked(items, group.eventKeys, 'push')}
                  onCheckedChange={(value) => onColumn('push', value)}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {sections.map((section) => {
              const sectionKeys = section.eventKeys.filter((key) => visibleKeySet.has(key));
              if (sectionKeys.length === 0) {
                return null;
              }
              return (
                <SectionRows
                  key={section.title || 'default'}
                  sectionTitle={section.title}
                  eventKeys={sectionKeys}
                  items={items}
                  emailColumnDisabled={emailColumnDisabled}
                  onPatch={onPatch}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MobilePreferenceList({
  group,
  sections,
  visibleKeySet,
  items,
  emailColumnDisabled,
  onColumn,
  onPatch,
}: {
  group: PreferenceGroup;
  sections: readonly ProfileNotificationTableSection[];
  visibleKeySet: Set<NotificationEventKey>;
  items: NotificationPreference[];
  emailColumnDisabled: boolean;
  onColumn: (field: PreferenceField, value: boolean) => void;
  onPatch: (eventKey: NotificationEventKey, field: PreferenceField, value: boolean) => void;
}) {
  const copy = useVoiceCopy();
  const present = usePresentVoiceText();
  return (
    <div className="space-y-4 md:hidden">
      <div className="rounded-md border bg-muted/30 p-3">
        <p className="mb-2 text-xs font-medium text-muted-foreground">Für alle Ereignisse</p>
        <div className="grid grid-cols-3 gap-2">
          {CHANNELS.map((channel) => (
            <ChannelToggle
              key={channel.field}
              icon={channel.icon}
              label={channel.label}
              ariaLabel={`${channel.label}, alle Ereignisse`}
              pressed={columnAllChecked(items, group.eventKeys, channel.field)}
              disabled={channel.field === 'email' && emailColumnDisabled}
              onPressedChange={(value) => onColumn(channel.field, value)}
            />
          ))}
        </div>
      </div>

      {sections.map((section) => {
        const sectionKeys = section.eventKeys.filter((key) => visibleKeySet.has(key));
        if (sectionKeys.length === 0) {
          return null;
        }
        return (
          <div key={section.title || 'default'} className="space-y-2">
            {section.title ? (
              <p className="px-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                {section.title}
              </p>
            ) : null}
            <ul className="space-y-2">
              {sectionKeys.map((eventKey) => {
                const item = items.find((entry) => entry.eventKey === eventKey);
                if (!item) {
                  return null;
                }
                const label = present(NOTIFICATION_EVENT_LABELS[eventKey]);
                const emailLocked = Boolean(item.emailLocked);
                const channelsLocked = Boolean(item.channelsLocked);
                return (
                  <li key={eventKey} className="rounded-md border bg-card p-3">
                    <p className="font-medium">{label}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {present(NOTIFICATION_EVENT_DESCRIPTIONS[eventKey])}
                    </p>
                    {emailLocked && eventKey === NotificationEventKey.ASSIGNED ? (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {copy('platform.profile.notify.assign-locked')}
                      </p>
                    ) : null}
                    {channelsLocked ? (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Glocke und Push bleiben an; E-Mail wird nicht versendet.
                      </p>
                    ) : null}
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      {CHANNELS.map((channel) => (
                        <ChannelToggle
                          key={channel.field}
                          icon={channel.icon}
                          label={channel.label}
                          ariaLabel={`${label}, ${channel.label}`}
                          pressed={item[channel.field]}
                          disabled={
                            channelsLocked ||
                            (channel.field === 'email' && (emailColumnDisabled || emailLocked))
                          }
                          onPressedChange={(value) => onPatch(eventKey, channel.field, value)}
                        />
                      ))}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function ChannelToggle({
  icon: Icon,
  label,
  ariaLabel,
  pressed,
  disabled,
  onPressedChange,
}: {
  icon: LucideIcon;
  label: string;
  ariaLabel: string;
  pressed: boolean;
  disabled?: boolean;
  onPressedChange: (value: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={pressed}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onPressedChange(!pressed)}
      className={cn(
        'flex min-h-11 flex-col items-center justify-center gap-1 rounded-md border px-1 py-2 text-xs font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50',
        pressed
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-input bg-background text-muted-foreground',
      )}
    >
      <Icon className="size-4" aria-hidden />
      <span>{label}</span>
    </button>
  );
}

function SectionRows({
  sectionTitle,
  eventKeys,
  items,
  emailColumnDisabled,
  onPatch,
}: {
  sectionTitle: string;
  eventKeys: NotificationEventKey[];
  items: NotificationPreference[];
  emailColumnDisabled: boolean;
  onPatch: (
    eventKey: NotificationEventKey,
    field: 'inApp' | 'email' | 'push',
    value: boolean,
  ) => void;
}) {
  const copy = useVoiceCopy();
  const present = usePresentVoiceText();
  return (
    <>
      {sectionTitle ? (
        <tr className="border-b bg-muted/20">
          <td colSpan={4} className="px-3 py-2 text-xs font-semibold tracking-wide uppercase">
            {sectionTitle}
          </td>
        </tr>
      ) : null}
      {eventKeys.map((eventKey) => {
        const item = items.find((entry) => entry.eventKey === eventKey);
        if (!item) {
          return null;
        }
        const label = present(NOTIFICATION_EVENT_LABELS[eventKey]);
        const description = present(NOTIFICATION_EVENT_DESCRIPTIONS[eventKey]);
        const emailLocked = Boolean(item.emailLocked);
        const channelsLocked = Boolean(item.channelsLocked);
        return (
          <tr key={eventKey} className="border-b last:border-b-0">
            <td className="sticky left-0 z-10 max-w-[14rem] bg-card px-3 py-2 align-top sm:max-w-xs">
              <p className="font-medium" title={description}>
                {label}
              </p>
              <p className="line-clamp-2 text-xs text-muted-foreground" title={description}>
                {description}
              </p>
              {emailLocked && eventKey === NotificationEventKey.ASSIGNED ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  {copy('platform.profile.notify.assign-locked')}
                </p>
              ) : null}
              {channelsLocked ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  Glocke und Push bleiben an; E-Mail wird nicht versendet.
                </p>
              ) : null}
            </td>
            <PreferenceCheckboxCell
              label={`${label}, Glocke`}
              checked={item.inApp}
              disabled={channelsLocked}
              onCheckedChange={(checked) => onPatch(eventKey, 'inApp', checked)}
            />
            <PreferenceCheckboxCell
              label={`${label}, E-Mail`}
              checked={item.email}
              disabled={emailColumnDisabled || emailLocked || channelsLocked}
              onCheckedChange={(checked) => onPatch(eventKey, 'email', checked)}
            />
            <PreferenceCheckboxCell
              label={`${label}, Push`}
              checked={item.push}
              disabled={channelsLocked}
              onCheckedChange={(checked) => onPatch(eventKey, 'push', checked)}
            />
          </tr>
        );
      })}
    </>
  );
}

function ColumnHeaderCheckbox({
  label,
  checked,
  disabled,
  onCheckedChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (value: boolean) => void;
}) {
  return (
    <label className="inline-flex items-center justify-center gap-2 font-medium">
      <Checkbox
        checked={checked}
        disabled={disabled}
        aria-label={`${label}, alle Zeilen`}
        onCheckedChange={(next) => onCheckedChange(next === true)}
      />
      <span>{label}</span>
    </label>
  );
}

function PreferenceCheckboxCell({
  label,
  checked,
  disabled,
  onCheckedChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (value: boolean) => void;
}) {
  return (
    <td className="px-3 py-2 text-center align-top">
      <Checkbox
        checked={checked}
        disabled={disabled}
        aria-label={label}
        onCheckedChange={(next) => onCheckedChange(next === true)}
      />
    </td>
  );
}

function PresetButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? 'default' : 'outline'}
      className={cn('h-8')}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}

function columnAllChecked(
  items: NotificationPreference[],
  eventKeys: readonly NotificationEventKey[],
  field: 'inApp' | 'email' | 'push',
): boolean {
  return eventKeys.every((eventKey) => {
    const item = items.find((entry) => entry.eventKey === eventKey);
    return item?.[field] === true;
  });
}

function pushStatusSentence(
  supported: boolean,
  serverEnabled: boolean,
  permission: PushPermissionState,
  isActiveOnThisDevice: boolean,
): string {
  if (!supported) {
    return 'Nicht unterstützt';
  }
  if (!serverEnabled) {
    return 'Derzeit nicht verfügbar';
  }
  if (permission === 'denied') {
    return 'Blockiert';
  }
  if (isActiveOnThisDevice) {
    return 'Aktiv auf diesem Gerät';
  }
  if (permission === 'granted') {
    return 'Berechtigung erteilt, noch nicht registriert';
  }
  return 'Nicht aktiv';
}
